<template>
  <div class="p-number-input">
    <input
      type="number"
      class="p-number-input__field"
      :value="value"
      :min="min"
      :max="max"
      :step="step"
      :style="fieldStyle"
      @input="onInput"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="onBlur"
    />
    <div class="p-number-input__spinner">
      <button
        type="button"
        class="p-number-input__spinner-btn"
        aria-label="increase"
        @mousedown="onIncrementStart"
        @mouseup="stopSpin"
        @mouseleave="stopSpin"
        @touchstart.prevent="onIncrementStart"
        @touchend="stopSpin"
      >▲</button>
      <button
        type="button"
        class="p-number-input__spinner-btn"
        aria-label="decrease"
        @mousedown="onDecrementStart"
        @mouseup="stopSpin"
        @mouseleave="stopSpin"
        @touchstart.prevent="onDecrementStart"
        @touchend="stopSpin"
      >▼</button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: [String, Number], default: '' },
    min: { type: [Number, String], default: 0 },
    max: { type: [Number, String], default: null },
    step: { type: Number, default: 1 },
    width: { type: [Number, String], default: 132 },
    height: { type: [Number, String], default: 32 },
  },
  emits: ['input'],
  computed: {
    fieldStyle() {
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width,
        height: typeof this.height === 'number' ? `${this.height}px` : this.height,
      };
    },
  },
  beforeUnmount() {
    this.stopSpin();
  },
  methods: {
    onKeydown(e) {
      // 指数記法/符号キーをブロック(e, E, +, -)
      if (['e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault();
      }
    },
    onFocus() {
      // フォーカス直前の値を保持しておく(blur 時に空欄なら復元)
      this._focusValue = this.value;
    },
    onBlur(e) {
      // 空欄のまま blur したら、フォーカス直前の値に戻す
      if (e.target.value === '' && this._focusValue !== '' && this._focusValue !== null && this._focusValue !== undefined) {
        // 親側の value が変わらないと Vue が input DOM を更新しないため、直接 DOM に書き込む
        e.target.value = String(this._focusValue);
        this.$emit('input', this._focusValue);
      }
    },
    onInput(e) {
      const raw = e.target.value;
      // 空入力は許可(親側でフィールドをクリアして初期値に戻すため)
      if (raw === '') {
        this.$emit('input', '');
        return;
      }
      const num = parseFloat(raw);
      // 無効な入力(数値化不可)は無視して emit しない
      if (isNaN(num)) {
        return;
      }
      // min/max の範囲内に clamp して emit
      // 親の value が clamp 後の値と同じだと Vue が DOM を更新しないため、明示的に反映
      const clamped = this.clamp(num);
      e.target.value = String(clamped);
      this.$emit('input', clamped);
    },
    currentNumber() {
      const n = parseFloat(this.value);
      return isNaN(n) ? 0 : n;
    },
    clamp(n) {
      const minVal = this.min === null || this.min === '' ? -Infinity : Number(this.min);
      const maxVal = this.max === null || this.max === '' ? Infinity : Number(this.max);
      return Math.min(Math.max(n, minVal), maxVal);
    },
    increment() {
      this.$emit('input', this.clamp(this.currentNumber() + this.step));
    },
    decrement() {
      this.$emit('input', this.clamp(this.currentNumber() - this.step));
    },
    startSpin(action) {
      this.stopSpin();
      action();
      this._spinTimeout = setTimeout(() => {
        this._spinInterval = setInterval(action, 50);
      }, 400);
    },
    stopSpin() {
      if (this._spinTimeout) {
        clearTimeout(this._spinTimeout);
        this._spinTimeout = null;
      }
      if (this._spinInterval) {
        clearInterval(this._spinInterval);
        this._spinInterval = null;
      }
    },
    onIncrementStart() {
      this.startSpin(this.increment);
    },
    onDecrementStart() {
      this.startSpin(this.decrement);
    },
  },
};
</script>

<style scoped>
.p-number-input {
    position: relative;
    display: inline-block;
}
.p-number-input__field {
    box-sizing: border-box;
    padding: 0 28px 0 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 13px;
    text-align: right;
    background: #fff;
}
.p-number-input__field::-webkit-outer-spin-button,
.p-number-input__field::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.p-number-input__field[type=number] {
    -moz-appearance: textfield;
}
.p-number-input__spinner {
    position: absolute;
    top: 1px;
    right: 1px;
    bottom: 1px;
    display: flex;
    flex-direction: column;
    width: 22px;
    border-left: 1px solid #eee;
    background: transparent;
}
.p-number-input__spinner-btn {
    flex: 1 1 0;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 8px;
    color: #bbb;
    line-height: 1;
}
.p-number-input__spinner-btn:hover {
    background: #f6f8fa;
    color: #57606a;
}
.p-number-input__spinner-btn:first-child {
    border-bottom: 1px solid #eee;
}
</style>
